import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  selectUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectAuthError,
  selectUserRole,
  login as loginAction,
  logout as logoutAction,
  fetchCurrentUser,
  clearError,
} from 'store/slices/auth.slice';
import { UserRole } from 'types/user.types';
import { ROUTES } from 'config/routes';
import { permissions } from 'utils/helpers';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const userRole = useAppSelector(selectUserRole);

  const hasRole = useCallback((roles: UserRole | UserRole[]) => {
    if (!userRole) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(userRole);
  }, [userRole]);

  const canAccess = useCallback((requiredRole?: UserRole) => {
    if (!userRole) return false;
    return permissions.canAccess(userRole, requiredRole);
  }, [userRole]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const result = await dispatch(loginAction({ email, password })).unwrap();
      navigate(ROUTES.DASHBOARD);
      return result;
    } catch (error) {
      throw error;
    }
  }, [dispatch, navigate]);

  const logout = useCallback(async () => {
    await dispatch(logoutAction());
    navigate(ROUTES.LOGIN);
  }, [dispatch, navigate]);

  const refreshUser = useCallback(async () => {
    if (isAuthenticated) {
      await dispatch(fetchCurrentUser());
    }
  }, [dispatch, isAuthenticated]);

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && !user) {
      refreshUser();
    }
  }, [isAuthenticated, user, refreshUser]);

  return {
    user,
    userRole,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
    clearAuthError,
    hasRole,
    canAccess,
    isAdmin: userRole === UserRole.ADMIN,
    isExecutor: userRole === UserRole.EXECUTOR,
    isViewer: userRole === UserRole.VIEWER,
    canManageUsers: permissions.canManageUsers(userRole),
    canManagePush: permissions.canManagePush(userRole),
    canViewStats: permissions.canViewStats(userRole),
  };
};